import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiDatePicker, MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  HTTP_STATUSES,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import {
  CalendarEventType,
  CalendarSetStatus,
  CalendarType,
  PlatformModules,
} from "@miplatform/types";
import {
  addToBusinessDate,
  getDate,
  toFormattedDate,
  toSnappedTime,
} from "@miplatform/utils";
import { mdiHandBackRightOutline } from "@mdi/js";
import { useCalendarSets } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";

const HOLD_UNITS = ["Hours", "Days"];

export const HoldOrderModal = ({ open, onClose, onHold, isLoading }) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const { calendarSets, setCalendarSets, getCalendarSets } = useCalendarSets({
    slimResponse: false,
  });

  const edit = useEdit({
    contactName: "",
    reason: "",
    calendarId: "",
    overrideHoldDuration: false,
    holdUnit: "Hours",
    holdValue: null,
    followUpDate: null,
    followUpTime: null,
  });

  const getActiveCalendarSets = async () => {
    const request = await getCalendarSets({
      filters: {
        status: CalendarSetStatus.Active,
        eventType: CalendarEventType.Order,
        module: PlatformModules.MiScheduling,
      },
    });

    if (request.status === HTTP_STATUSES.OK) {
      setCalendarSets(request.data);
    }
  };

  const holdCalendars = useMemo(() => {
    const options = [];

    calendarSets.forEach((set) =>
      set.calendarConfigs.forEach((calendar) => {
        if (calendar.calendarType === CalendarType.HoldsList) {
          options.push({
            label: calendar.name,
            calendarSetName: set.name,
            value: calendar.id,
            ...calendar,
          });
        }
      })
    );

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [calendarSets]);

  const calendarId = edit.getValue("calendarId");
  const selectedCalendar = holdCalendars?.find((x) => x.id === calendarId);
  const followUpDefaultOffsetHours =
    selectedCalendar?.followUpDefaultOffsetHours || null;
  const getDefaultHoldDateTimeUpdates = () => {
    if (!selectedCalendar || !followUpDefaultOffsetHours) {
      return {
        followUpDate: null,
        followUpTime: null,
        holdValue: null,
      };
    }
    const followUpDateTime = addToBusinessDate(
      getDate(),
      followUpDefaultOffsetHours,
      "hours"
    );
    const snappedDateTime = toSnappedTime(followUpDateTime);
    return {
      followUpDate: snappedDateTime,
      followUpTime: toFormattedDate(
        snappedDateTime,
        DATE_TIME_FORMATS.FORMAT_24_HOUR
      ),
      holdValue: followUpDefaultOffsetHours,
    };
  };

  useEffect(() => {
    edit.update(getDefaultHoldDateTimeUpdates());
  }, [calendarId]);

  useEffect(() => {
    getActiveCalendarSets();
  }, []);

  const holdDetailFields = [
    {
      label: "Contact/Customer Name",
      name: "contactName",
      required: true,
      width: "100%",
      style: {
        paddingLeft: 0,
        paddingRight: 0,
      },
    },
    {
      label: "Hold Type",
      name: "calendarId",
      fieldType: INPUT_TYPES.SELECT,
      style: { paddingLeft: 0, paddingRight: 0 },
      renderOption: (option) => (
        <Box key={option.value} display="flex">
          <Typography>{`${option.label}`}</Typography>
          <Typography
            color="gray"
            ml={1}
          >{`(${option.calendarSetName})`}</Typography>
        </Box>
      ),
      width: "100%",
      options: holdCalendars || [],
      required: true,
    },
    {
      label: "Reason",
      name: "reason",
      width: "100%",
      required: true,
      style: { paddingLeft: 0, paddingRight: 0 },
    },
    {
      label: "Hold Unit",
      name: "holdUnit",
      fieldType: INPUT_TYPES.SELECT,
      className: classes.orangeInputBox,
      options: HOLD_UNITS.map((unit) => ({
        label: unit,
        value: unit,
      })),
      handleChange: (e) => {
        const holdUnit = e.target.value;
        const updatedDateTime = addToBusinessDate(
          getDate(),
          edit.getValue("holdValue"),
          holdUnit
        );
        const snappedDateTime = toSnappedTime(updatedDateTime);

        edit.update({
          holdUnit,
          followUpDate: snappedDateTime,
          followUpTime: toFormattedDate(
            snappedDateTime,
            DATE_TIME_FORMATS.FORMAT_24_HOUR
          ),
        });
      },
      style: {
        paddingLeft: 0,
        paddingRight: theme.spacing(0.5),
      },
    },
    {
      label: "Hold Value",
      name: "holdValue",
      fieldType: INPUT_TYPES.NUMERIC,
      allowNegative: false,
      thousandSeparator: true,
      allowLeadingZeros: false,
      decimalScale: 0,
      handleChange: ({ event: e = {} }) => {
        const holdValue = e.target?.value;
        const updatedDateTime = addToBusinessDate(
          getDate(),
          holdValue,
          edit.getValue("holdUnit")
        );
        const snappedDateTime = toSnappedTime(updatedDateTime);

        edit.update({
          holdValue,
          followUpDate: snappedDateTime,
          followUpTime: toFormattedDate(
            snappedDateTime,
            DATE_TIME_FORMATS.FORMAT_24_HOUR
          ),
        });
      },
      style: {
        paddingLeft: theme.spacing(2),
        paddingRight: 0,
      },
      inputTextboxProps: {
        className: classes.orangeInputBox,
      },
    },
    {
      label: "Follow-Up Date",
      name: "followUpDate",
      fieldType: INPUT_TYPES.CUSTOM,
      style: {
        paddingLeft: 0,
        paddingTop: theme.spacing(2),
        paddingRight: theme.spacing(1.2),
      },
      customComponent: <MiDatePicker />,
      disabled: true,
    },
    {
      label: "Follow-Up Time",
      name: "followUpTime",
      fieldType: INPUT_TYPES.TIME,
      style: {
        paddingLeft: theme.spacing(1),
        paddingRight: 0,
        paddingTop: theme.spacing(1),
      },
      sx: {
        "& .MuiInputLabel-root": {
          left: theme.spacing(1),
        },
      },
      disabled: true,
    },
    {
      label: "Override default hold duration",
      name: "overrideHoldDuration",
      fieldType: INPUT_TYPES.CHECKBOX,
      handleChange: ({ target: { checked } }) => {
        if (checked) {
          edit.update({
            overrideHoldDuration: checked,
            holdUnit: "Hours",
          });
        } else {
          edit.update({
            overrideHoldDuration: checked,
            holdUnit: "Hours",
            ...getDefaultHoldDateTimeUpdates(),
          });
        }
      },
      width: "100%",
      style: { padding: theme.spacing(0.5, 0) },
    },
  ];

  const getDisplayedFields = () => {
    if (edit.getValue("overrideHoldDuration")) {
      return [
        ...holdDetailFields.slice(0, 3),
        holdDetailFields.at(-1),
        ...holdDetailFields.slice(3, -1),
      ];
    }
    return holdDetailFields.filter(
      ({ name }) => !["holdUnit", "holdValue"].includes(name)
    );
  };

  const handleSubmit = async () => {
    const { contactName, reason, followUpDate, calendarId } = edit.getValue();
    await onHold({
      contactName,
      reason,
      followUpDate: followUpDate
        ? toFormattedDate(
            followUpDate,
            DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
          )
        : "",
      calendarId,
    });
    edit.reset();
  };

  const handleClose = () => {
    edit.reset();
    onClose();
  };

  useEffect(() => {
    if (open) {
      edit.reset();
    }
  }, [open]);

  return (
    <MiModal
      open={open}
      closeModal={handleClose}
      width={448}
      titleIcon={mdiHandBackRightOutline}
      leftIconColor={theme.palette.purple.dark}
      title="Hold Order"
      loading={isLoading}
      description="Holding an order will automatically place all assignments on hold."
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Hold Order",
          variant: BUTTON_VARIANTS.CONTAINED,
          color: BUTTON_COLORS.PRIMARY,
          disabled:
            isLoading || !edit.allFilled("contactName", "calendarId", "reason"),
          onClick: handleSubmit,
        },
      ]}
    >
      <Box component="form">
        <MiDetailFields edit={edit} detailFields={getDisplayedFields()} />
      </Box>
    </MiModal>
  );
};

const useStyles = makeStyles()((theme) => ({
  orangeInputBox: {
    width: "100%",
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.orange.light,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.main,
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.dark,
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.orange.main,
    },
  },
}));
